import React, { forwardRef, useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import { SegmentContextProvider } from './context'
import useControllableState from '../hooks/useControllableState'
import { useForm } from '../Form/context'
import { useInputGroup } from '../InputGroup/context'

const Segment = forwardRef((props, ref) => {
    const {
        value: valueProp,
        defaultValue,
        onChange = () => { },
        children,
        className,
        selectionType,
        size,
        readOnly = false,
        disabled = false,
        ...rest
    } = props

    const formControl = useForm()
    const inputGroupControl = useInputGroup()
    const { controlSize } = 0

    const [value, setValue] = useControllableState({
        prop: valueProp,
        defaultProp: defaultValue,
        onChange: onChange,
        selectionType,
    })

    const onActive = useCallback(
        (itemValue) => {
            if (!readOnly && !disabled) {
                setValue(itemValue)
            }
        },
        [setValue, readOnly, disabled]
    )

    const onDeactivate = useCallback(
        (itemValue) => {
            if (!readOnly && !disabled) {
                if (selectionType === 'single') {
                    setValue('')
                }

                if (selectionType === 'multiple') {
                    setValue((prevValue = []) => {
                        return prevValue.filter((value) => value !== itemValue)
                    })
                }
            }
        },
        [setValue, selectionType, readOnly, disabled]
    )

    const segmentValue = useMemo(() => {
        if (selectionType === 'single') {
            if (value && typeof value === 'string') {
                return [value]
            }

            if (value && Array.isArray(value)) {
                return value
            }

            return []
        }

        if (selectionType === 'multiple') {
            return value ? value : []
        }
    }, [selectionType, value])

    return (
        <SegmentContextProvider
            value={{
                value: segmentValue,
                onActive: onActive,
                onDeactivate: onDeactivate,
                size:
                    size ||
                    inputGroupControl?.size ||
                    formControl?.size || controlSize,
                selectionType,
                readOnly,
                disabled,
            }}
        >
            <div
                ref={ref}
                className={classNames('segment', className, { 'segment--disabled': disabled })}
                {...rest}
            >
                {children}
            </div>
        </SegmentContextProvider>
    )
})

Segment.defaultProps = {
    selectionType: 'single',
}

Segment.propTypes = {
    selectionType: PropTypes.oneOf(['single', 'multiple']),
    value: PropTypes.arrayOf(PropTypes.string),
    defaultValue: PropTypes.arrayOf(PropTypes.string),
    readOnly: PropTypes.bool,
    disabled: PropTypes.bool,
}

export default Segment
